// src/modules/Rules/components/display/RuleActionsSection.tsx
// Sección del formulario para configurar las acciones que ejecuta una regla de envío.
// Soporta: "Enviar con courier" (courier_integration_id) y "No enviar" (no_enviar).
import { faBullseye } from '@fortawesome/pro-regular-svg-icons';
import { SectionBox } from '@shared/components/ui/SectionBox';
import { Label } from '@shared/components/ui/shadcn/label';
import { RadioGroup, RadioGroupItem } from '@shared/components/ui/shadcn/radio-group';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Combobox } from '@shared/components/ui/AdvancedFilter/components';
import { FormSectionCard } from './FormSectionCard';

export type ActionType = 'courier_integration_id' | 'no_enviar';

interface Integration {
  id: number;
  name: string;
}
interface Product {
  id: number;
  name: string;
}

interface RuleActionsSectionProps {
  actionType: ActionType;
  onActionTypeChange: (type: ActionType) => void;
  courierIntegrations: Integration[];
  courierProducts: Product[];
  loadingProducts: boolean;
  selectedCourierIntegrationId: string;
  onCourierChange: (id: string) => void;
  selectedCourierProductId: string;
  onProductChange: (id: string) => void;
  isReadOnly?: boolean;
  sectionStyle?: 'default' | 'clientCard';
}

const RuleActionsSection: React.FC<RuleActionsSectionProps> = ({
  actionType,
  onActionTypeChange,
  courierIntegrations,
  courierProducts,
  loadingProducts,
  selectedCourierIntegrationId,
  onCourierChange,
  selectedCourierProductId,
  onProductChange,
  isReadOnly = false,
  sectionStyle = 'default',
}) => {
  const { t } = useTranslation('rulesModule');

  const courierOptions = courierIntegrations.map((c) => ({
    label: c.name,
    value: String(c.id),
  }));

  const productOptions = courierProducts.map((p) => ({
    label: p.name,
    value: String(p.id),
  }));

  const showCourierFields = actionType === 'courier_integration_id';

  const inner = (
    <>
      {sectionStyle === 'default' && <h2 className="text-lg font-semibold mb-2">{t('create.actions_title')}</h2>}

      {/* Selector de tipo de acción */}
      <div className="mb-4">
        <Label className="mb-2 block">{t('create.action_type_label')}</Label>
        <RadioGroup
          value={actionType}
          onValueChange={(v) => onActionTypeChange(v as ActionType)}
          className="flex flex-col gap-2"
          disabled={isReadOnly}
        >
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="courier_integration_id" id="action-courier" />
            <Label htmlFor="action-courier" className="font-normal cursor-pointer">
              {t('create.action_send_with_courier')}
            </Label>
          </div>
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="no_enviar" id="action-no-enviar" />
            <Label htmlFor="action-no-enviar" className="font-normal cursor-pointer">
              {t('create.action_no_enviar')}
            </Label>
          </div>
        </RadioGroup>
      </div>

      {/* Campos de courier/producto — solo cuando acción es "Enviar con courier" */}
      {showCourierFields && (
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="courier" className="mb-2 block">
              {t('create.send_with')}
            </Label>
            <Combobox
              options={courierOptions}
              placeholder={t('create.select_courier')}
              value={selectedCourierIntegrationId}
              setValue={onCourierChange}
              className="w-full"
              dataTestId="courier-combobox"
              disabled={isReadOnly}
            />
          </div>

          <div>
            <Label htmlFor="product" className="mb-2 block">
              {t('create.product')}
            </Label>
            <Combobox
              options={productOptions}
              placeholder={t('create.select_product')}
              value={selectedCourierProductId}
              setValue={onProductChange}
              disabled={isReadOnly || !selectedCourierIntegrationId || loadingProducts || courierProducts.length === 0}
              className="w-full"
              dataTestId="product-combobox"
            />
          </div>
        </div>
      )}
    </>
  );

  if (sectionStyle === 'clientCard') {
    return (
      <FormSectionCard title={t('clientList.display.action')} icon={faBullseye} accent="left">
        {inner}
      </FormSectionCard>
    );
  }

  return <SectionBox>{inner}</SectionBox>;
};

export default RuleActionsSection;
